import type {SettlementPaymentMethod} from '@afriex/core/domain/types';

import axios from '@/api/axios';
import type {
  AdminPaginatedEnvelope,
  SettlementPaymentMethodListParams,
  SettlementPaymentMethodPayload,
} from '@/types';

/**
 * Afriex's own settlement payment methods: the accounts a fee invoice is paid
 * into. The server owns this contract, and responses are typed at the axios
 * call rather than unwrapped defensively.
 */
const settlementPaymentMethodEndpoint = '/v2/admin/settlement-payment-methods';

export const settlementAccountService = {
  getSettlementPaymentMethods: async (
    params?: SettlementPaymentMethodListParams,
  ): Promise<AdminPaginatedEnvelope<SettlementPaymentMethod[]>> => {
    const response = await axios.get<
      AdminPaginatedEnvelope<SettlementPaymentMethod[]>
    >(settlementPaymentMethodEndpoint, {
      params: {page: 0, limit: 10, ...params},
    });
    return response.data;
  },

  createSettlementPaymentMethod: async (
    payload: SettlementPaymentMethodPayload,
  ): Promise<void> => {
    await axios.post(settlementPaymentMethodEndpoint, payload);
  },

  updateSettlementPaymentMethodById: async (
    id: string,
    payload: Partial<SettlementPaymentMethodPayload>,
  ): Promise<SettlementPaymentMethod> => {
    // PATCH accepts a genuine partial server-side, so a one-field update (e.g.
    // deactivating via meta.flags) is valid.
    const response = await axios.patch<SettlementPaymentMethod>(
      `${settlementPaymentMethodEndpoint}/${id}`,
      payload,
    );
    return response.data;
  },
};

export type SettlementAccountService = typeof settlementAccountService;
